import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Flex,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { TbChevronLeft, TbChevronRight, TbDownload } from 'react-icons/tb';
import {
    useInventoryReportingSummary,
    useInventoryReportingTimeseries,
} from '../../../hooks/reporting';
import { useTableSettings } from '../../../hooks/useTableSettings';
import { useWidgetDashboard } from '../widgets/useWidgetDashboard';
import WidgetGrid, { type WidgetGridHandle } from '../widgets/WidgetGrid';
import InventoryWidgetRenderer, {
    type InventoryWidgetDataContext,
} from '../widgets/InventoryWidgetRenderer';
import {
    INVENTORY_CATALOG,
    INVENTORY_STORAGE_KEY,
    INVENTORY_LAYOUT_VERSION,
    getDefaultInventoryWidgets,
    sanitizeInventoryWidget,
} from '../widgets/inventoryCatalog';
import { exportToCsv } from '../../../utils/csvExport';
import type { WidgetSharedParams } from '../widgets/useWidgetData';
import { getReportingComparison } from '../shared/reportingComparisonUtils';
import { useAuditingInventory } from '../components/useAuditingInventory';
import { useReportingInventoryFilters } from '../components/useReportingInventoryFilters';
import ReportingInventoryFilters from '../components/ReportingInventoryFilters';
import ReportingTableSettingsDrawer from '../components/ReportingTableSettingsDrawer';

interface InventoryTabProps {
    startDate: string;
    endDate: string;
    locationId?: string;
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
    preset?: string;
    isActive?: boolean;
    onRegisterAddWidget?: (fn: () => void) => void;
}

const InventoryTab = ({
    startDate,
    endDate,
    locationId,
    granularity = 'week',
    preset,
    isActive = true,
    onRegisterAddWidget,
}: InventoryTabProps) => {
    const gridRef = useRef<WidgetGridHandle>(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (onRegisterAddWidget) {
            onRegisterAddWidget(() => gridRef.current?.openAddWidget());
        }
    }, [onRegisterAddWidget]);

    const comparison = getReportingComparison(preset);

    const filters = useReportingInventoryFilters({
        locationId,
        isActive,
        searchQuery,
    });

    const inventory = useAuditingInventory({
        startDate,
        endDate,
        locationId,
        preset,
        isActive,
        sharedFilters: filters,
    });

    const { settings: tableSettings, updateSettings: updateTableSettings } =
        useTableSettings('reporting_inventory_dashboard');

    const apiFilters = filters.apiFilters;

    const { data: summaryData, isFetching: summaryFetching, isError: summaryError } =
        useInventoryReportingSummary(startDate, endDate, {
            ...apiFilters,
            preset,
            comparison,
            enabled: isActive,
        });

    const { widgets, addWidget, updateWidget, removeWidget, reorderWidgets } = useWidgetDashboard(
        false,
        {
            storageKey: INVENTORY_STORAGE_KEY,
            layoutVersion: INVENTORY_LAYOUT_VERSION,
            getDefaults: getDefaultInventoryWidgets,
            sanitizeWidget: sanitizeInventoryWidget,
        },
    );

    const needsRestockSeries = widgets.some((w) => {
        const isInStock =
            w.metric === 'inStockQuantity'
            || w.metric === 'avgInStockQuantity'
            || w.metric === 'medianInStockQuantity';
        return isInStock && w.restockItemsOnly;
    });

    const { data: timeseriesData, isFetching: timeseriesFetching } =
        useInventoryReportingTimeseries(startDate, endDate, {
            ...apiFilters,
            granularity,
            preset,
            comparison,
            enabled: isActive,
        });

    const { data: restockTimeseriesData, isFetching: restockTimeseriesFetching } =
        useInventoryReportingTimeseries(startDate, endDate, {
            ...apiFilters,
            granularity,
            preset,
            comparison,
            restockAlert: true,
            enabled: isActive && needsRestockSeries,
        });

    const widgetData: InventoryWidgetDataContext = {
        summary: summaryData,
        summaryLoading: summaryFetching && !summaryData,
        summaryError: !!summaryError,
        timeseries: timeseriesData,
        timeseriesLoading: timeseriesFetching && !timeseriesData,
        restockTimeseries: restockTimeseriesData,
        restockTimeseriesLoading: restockTimeseriesFetching && !restockTimeseriesData,
        comparison,
        granularity,
        preset,
        startDate,
        endDate,
    };

    const sharedParams: WidgetSharedParams = useMemo(
        () => ({
            startDate,
            endDate,
            locationId,
            granularity,
            preset,
            comparison,
        }),
        [startDate, endDate, locationId, granularity, preset, comparison],
    );

    const getWidgetRefetching = (config: (typeof widgets)[number]) => {
        if (config.widgetType === 'keyMetric') {
            return summaryFetching && !!summaryData;
        }
        if (config.widgetType === 'timeSeries') {
            const isInStock =
                config.metric === 'inStockQuantity'
                || config.metric === 'avgInStockQuantity'
                || config.metric === 'medianInStockQuantity';
            if (isInStock && config.restockItemsOnly) {
                return restockTimeseriesFetching && !!restockTimeseriesData;
            }
            return timeseriesFetching && !!timeseriesData;
        }
        return false;
    };

    return (
        <VStack spacing={6} align="stretch" pb={8}>
            <ReportingInventoryFilters
                filters={filters}
                hideLocation={Boolean(locationId)}
            />

            <WidgetGrid
                ref={gridRef}
                widgets={widgets}
                params={sharedParams}
                onUpdate={updateWidget}
                onRemove={removeWidget}
                onAdd={addWidget}
                onReorder={reorderWidgets}
                isGm={false}
                editorCatalog={INVENTORY_CATALOG}
                renderWidget={(config) => (
                    <InventoryWidgetRenderer config={config} data={widgetData} />
                )}
                getWidgetRefetching={getWidgetRefetching}
                onDownloadWidget={(config) => {
                    if (config.dataSource === 'timeseries') {
                        const isInStock =
                            config.metric === 'inStockQuantity'
                            || config.metric === 'avgInStockQuantity'
                            || config.metric === 'medianInStockQuantity';
                        const series =
                            isInStock && config.restockItemsOnly && restockTimeseriesData
                                ? restockTimeseriesData
                                : timeseriesData;
                        if (series) {
                            exportToCsv(
                                series.data,
                                config.title.replace(/\s+/g, '_').toLowerCase(),
                            );
                        }
                    } else if (config.dataSource === 'summary' && summaryData) {
                        const { byTagLocation: _byTag, ...summaryForExport } = summaryData;
                        exportToCsv(
                            [summaryForExport],
                            config.title.replace(/\s+/g, '_').toLowerCase(),
                        );
                    }
                }}
            />

            <Box
                borderTopWidth="1px"
                borderColor="gray.200"
                pt={4}
            >
                <Flex align="center" gap={3} mb={3} flexWrap="wrap">
                    <Text fontSize="md" fontWeight="semibold" color="gray.800" flexShrink={0}>
                        Inventory Changes
                    </Text>
                    <InputGroup maxW="280px" size="sm">
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="gray.400" boxSize={3.5} />
                        </InputLeftElement>
                        <Input
                            placeholder="Search name or part #"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                inventory.setPage(1);
                            }}
                            bg="white"
                        />
                    </InputGroup>
                    <Box flex={1} />
                    <ReportingTableSettingsDrawer
                        settings={tableSettings}
                        onUpdateSettings={updateTableSettings}
                        columns={inventory.drawerColumns}
                        onCommitColumns={inventory.handleCommitColumns}
                        useAccordionLayout
                        hideDisplayTab
                    />
                    <Menu>
                        <Tooltip label="Export" placement="top" hasArrow fontSize="xs">
                            <MenuButton
                                as={IconButton}
                                aria-label="Export"
                                icon={<Icon as={TbDownload as React.ElementType} boxSize={4} />}
                                size="sm"
                                variant="outline"
                                borderColor="gray.300"
                                color="gray.600"
                                isLoading={inventory.isExporting}
                            />
                        </Tooltip>
                        <MenuList minW="140px" zIndex={20}>
                            <MenuItem fontSize="sm" onClick={() => inventory.handleExport('csv')}>
                                Download CSV
                            </MenuItem>
                            <MenuItem fontSize="sm" onClick={() => inventory.handleExport('xlsx')}>
                                Download Excel
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </Flex>

                <Box
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    overflow="hidden"
                    bg="white"
                >
                    {inventory.table}
                </Box>

                <Flex align="center" justify="flex-end" gap={2} mt={3}>
                    <Text fontSize="sm" color="gray.500">
                        Page {inventory.page} of {inventory.totalPages}
                    </Text>
                    <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<Icon as={TbChevronLeft as React.ElementType} />}
                        isDisabled={inventory.page <= 1}
                        onClick={() => inventory.setPage(Math.max(1, inventory.page - 1))}
                    >
                        Prev
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        rightIcon={<Icon as={TbChevronRight as React.ElementType} />}
                        isDisabled={inventory.page >= inventory.totalPages}
                        onClick={() =>
                            inventory.setPage(Math.min(inventory.totalPages, inventory.page + 1))
                        }
                    >
                        Next
                    </Button>
                </Flex>
            </Box>
        </VStack>
    );
};

export default InventoryTab;
